import { IncomingHttpHeaders } from "http"
import { FORWARD_HEADER_PREFIX } from "@/proxy-utils"
import axios, {
  AxiosError,
  AxiosInstance,
  CreateAxiosDefaults,
  RawAxiosRequestHeaders,
} from "axios"
import axiosRetry, { IAxiosRetryConfig } from "axios-retry"

import { env } from "@/lib/env"

declare module "axios" {
  interface InternalAxiosRequestConfig {
    voiceTraceStartedAt?: number
  }
}

interface AxiosLightErrorDetails {
  cause?: Error | unknown
  request?: {
    baseUrl?: string
    url?: string
    method?: string
    startedAt?: number
  }
  response?: {
    status?: number
    statusText?: string
    data?: unknown
    headers?: unknown
  }
}

export class AxiosLightError extends Error {
  readonly name = "AxiosLightError"
  cause?: Error | unknown
  request?: AxiosLightErrorDetails["request"]
  response?: AxiosLightErrorDetails["response"]
  isCancelled: boolean

  constructor(
    message: string,
    isCancelled: boolean,
    details: AxiosLightErrorDetails
  ) {
    super(message)
    this.cause = details.cause
    this.request = details.request
    this.response = details.response
    this.isCancelled = isCancelled
  }
}

export function axiosInstance(
  params: CreateAxiosDefaults,
  retries = 2,
  retryDelayFactor = 100
): AxiosInstance {
  const instance = axios.create(params)

  const retryConfig: IAxiosRetryConfig = {
    retries,
    retryDelay: (retryCount: number, error: AxiosError) =>
      axiosRetry.exponentialDelay(retryCount, error, retryDelayFactor),
    retryCondition: (error: AxiosError) => {
      // Override default so that we don't retry 429 or 500 errors
      // which axios retries by default. We DO retry 501+ errors.
      // 429 might make sense to retry if our server generated a
      // retry-after header (wich axios-retry uses, although I'm not
      // sure of the logic). But we don't want to expose that number
      // and would rather fail and trigger UI that's expecting it.
      const status = error.response?.status
      return (
        axiosRetry.isNetworkOrIdempotentRequestError(error) &&
        (!status || (status >= 501 && status <= 599))
      )
    },
  }

  axiosRetry(instance, retryConfig)
  instance.interceptors.response.use(
    function (response) {
      // If the request is successful, we don't have to do anything and just return the response
      return response
    },
    function (error) {
      if (error instanceof AxiosLightError) {
        return Promise.reject(error)
      }

      let lightError: AxiosLightError
      if (error instanceof AxiosError) {
        lightError = new AxiosLightError(error.message, axios.isCancel(error), {
          cause: error.cause,
          request: {
            baseUrl: error.request?.baseURL ?? error.config?.baseURL,
            url: error.request?.url ?? error.config?.url,
            method: error.request?.method ?? error.config?.method,
            startedAt: error.config?.voiceTraceStartedAt,
          },
          response: {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            headers: error.response?.headers,
          },
        })
      } else {
        let message = "Unknown error"
        if (error instanceof Error) {
          message = error.message
        } else if (typeof error === "string") {
          message = error
        } else if (error && typeof error === "object" && "message" in error) {
          message = error.message
        } else {
          message = String(error)
        }
        lightError = new AxiosLightError(message, false, { cause: error })
      }

      // Then reject the promise with the modified error so that it can be caught and handled downstream
      return Promise.reject(lightError)
    }
  )
  return instance
}

// forwards the original headers, filters out some like cookie, authorization,
// let those more sensitive headers be an explicit forward from the caller
export function forwardHeaders(
  incomingHeaders: IncomingHttpHeaders,
  outgoingHeaders: RawAxiosRequestHeaders
) {
  const disallowedHeaders = ["cookie", "authorization"]
  Object.keys(incomingHeaders).forEach((key) => {
    const lowercaseKey = key.toLowerCase()
    if (!disallowedHeaders.includes(lowercaseKey)) {
      if (!lowercaseKey.startsWith(FORWARD_HEADER_PREFIX)) {
        outgoingHeaders[FORWARD_HEADER_PREFIX + lowercaseKey] =
          incomingHeaders[key]
      }
    }
  })
}

export function apiBaseUrl(): string {
  let url = env("NEXT_PUBLIC_API_SERVER_URL")
  if (!url) {
    throw new Error("NEXT_PUBLIC_API_SERVER_URL is not defined")
  }

  while (url.endsWith("/")) {
    url = url.substring(0, url.length - 1)
  }

  // Dev on another device (a phone at the Mac's .local name): the configured
  // localhost API is the phone itself, so go through the dev server's
  // same-origin /mt-api proxy (next.config.js rewrites) instead.
  // This frame's own origin is the dev server, even when embedded.
  // eslint-disable-next-line no-restricted-properties
  const page = typeof window !== "undefined" ? window.location : undefined
  if (
    env("NODE_ENV") === "development" &&
    page &&
    isLoopback(new URL(url).hostname) &&
    !isLoopback(page.hostname)
  ) {
    return `${page.origin}/mt-api`
  }

  return url
}

function isLoopback(hostname: string) {
  return (
    hostname === "localhost" ||
    hostname.endsWith(".localhost") ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]"
  )
}
